import { Suspense, useEffect, useLayoutEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, BrowserRouter as Router } from "react-router-dom";

import Alert from "./components/alert/Alert";
import Loading from "./components/alert/Loading";
import PageRender from "./customRouter/PageRender";
import PrivateRouter from "./customRouter/PrivateRouter";
import Home from "./pages/home";
import { refreshToken } from "./redux/actions/authAction";
import SocketClient from "./SocketClient";

// Performance monitoring hook
const usePerformanceMonitoring = () => {
	useEffect(() => {
		// Track app load time
		const loadTime = performance.now();
		console.log(`App loaded in ${loadTime.toFixed(2)}ms`);

		// Track memory usage
		if (performance.memory) {
			console.log(
				`Initial memory usage: ${(
					performance.memory.usedJSHeapSize /
					1024 /
					1024
				).toFixed(2)}MB`
			);
		}
	}, []);
};

// Notification permission hook
const useNotificationPermission = () => {
	useEffect(() => {
		if (!("Notification" in window)) {
			console.warn("This browser does not support desktop notification");
			return;
		}

		if (Notification.permission === "granted") {
			return;
		}

		if (Notification.permission !== "denied") {
			Notification.requestPermission().then((permission) => {
				if (permission === "granted") {
					console.log("Notification permission granted");
				}
			});
		}
	}, []);
};

// Scroll restoration hook
const useScrollRestoration = () => {
	useEffect(() => {
		window.history.scrollRestoration = "manual";
	}, []);
};

// Auth token management hook
const useAuthToken = () => {
	const dispatch = useDispatch();
	const { auth } = useSelector((state) => state);
	const firstLogin = localStorage.getItem("firstLogin");

	useLayoutEffect(() => {
		if (firstLogin) {
			dispatch(refreshToken());
		}
	}, [dispatch, firstLogin]);

	useEffect(() => {
		if (auth.token) {
			// Future: Add token-based API calls here
			// dispatch(getPosts(auth.token));
			// dispatch(getSuggestions(auth.token));
			// dispatch(getNotifies(auth.token));
		}
	}, [dispatch, auth.token]);

	return { auth };
};

// Route configuration
const ROUTE_CONFIG = [
	{
		path: "/:page",
		exact: true,
		component: PageRender,
		private: true,
	},
	{
		path: "/:page/:id",
		exact: true,
		component: PageRender,
		private: true,
	},
	{
		path: "/",
		exact: true,
		component: Home,
		private: false,
	},
];

// Main App component
function App() {
	const { auth, status, modal } = useSelector((state) => state);
	// const dispatch = useDispatch();

	// Custom hooks for better organization
	// usePerformanceMonitoring();
	useNotificationPermission();
	useScrollRestoration();
	const { auth: authData } = useAuthToken();

	// Memoized app class
	const appClassName = useMemo(() => {
		return `App ${(status || modal) && "mode"}`;
	}, [status, modal]);

	// Memoized main content visibility
	const showMainContent = useMemo(() => {
		const currentUrl = window.location.href;
		return (
			currentUrl.indexOf("pdfreader") <= -1 &&
			currentUrl.indexOf("examination") <= -1
		);
	}, []);

	// Memoized socket client
	const SocketClientComponent = useMemo(() => {
		return auth.token ? <SocketClient /> : null;
	}, [auth.token]);

	return (
		<Router>
			<Alert />
			<input type="checkbox" id="theme" />
			<div className={appClassName}>
				{showMainContent && (
					<div className="main">
						<div b-z34ddepl3z="true" id="app" style={{ zIndex: 100 }} />
					</div>
				)}

				<Suspense fallback={<Loading />}>
					<div className="wrap_page">
						{ROUTE_CONFIG.map((route, index) => {
							const RouteComponent = route.private ? PrivateRouter : Route;
							return (
								<RouteComponent
									key={`${route.path}-${index}`}
									exact={route.exact}
									path={route.path}
									component={route.component}
								/>
							);
						})}
					</div>
				</Suspense>
			</div>
		</Router>
	);
}

export default App;
